import { useCallback } from 'react';
import { Category, ProductSearchQuery, Result } from 'shared/types/product';
import { Inventory } from 'shared/types/product/inventory';
import { ProductQuery, SearchQueryVariants, SortAttributes } from 'shared/types/query/ProductQuery';
import useSWR from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';
import { ProductData, ReviewData, ReviewDraft, UseProductReturn } from './types';
import paymentService from 'service/payment.service';
import { ProductActions, ReviewsActions } from 'components/genius-ui/organisms/product/product-list/context/constants';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { useLocale } from '../useLocale';

export interface ReviewRequestData {
  key?: string;
  authorName?: string;
  title: string;
  text: string;
  reviewSource: string;
  rating: number;
  typeId: string;
  id?: string;
}
export interface Action {
  key?: string;
  id: string;
  version: number;
  action?: string;
  rating: number;
}

const useProduct = (): UseProductReturn => {
  const { locale } = useLocale();
  const extensions = sdk.composableCommerce;

  const categoriesResults = useSWR(
    '/action/product/queryCategories',
    () => extensions.product.queryCategories({ limit: 200 }),
    revalidateOptions,
  );

  const categories = (categoriesResults.data?.isError ? [] : (categoriesResults.data?.data?.items as Category[])) ?? [];

  const query = useCallback(async (productQuery: ProductQuery) => {
    const extensions = sdk.composableCommerce;
    const sortAttributes = productQuery.sortAttributes;
    const attributeFilters = productQuery.attributeFilters || [];
    const queryPayload: any = {
      limit: productQuery.limit,
      query: productQuery.query,
      cursor: productQuery.cursor,
      categories: productQuery.categories,
      categoryName: productQuery.categoryName,
      hideSoldOutProducts: productQuery.hideSoldOutProducts,
      skus: productQuery.skus,
    };

    if (sortAttributes) {
      Object.entries(sortAttributes).forEach(([index, attr]) => {
        if (attr && typeof attr === 'object') {
          Object.entries(attr).forEach(([key, value]) => {
            // const formattedKey = key === 'name' ? 'name.de-DE' : key;
            queryPayload[`sortAttributes[${index}][${key}]`] = String(value);
          });
        }
      });
    }
    if (attributeFilters.length > 0) {
      const dynamicFilters = attributeFilters.map((filter) => ({
        type: filter.type || 'string',
        name: filter.name,
        value: Array.isArray(filter.values) ? filter.values.join(',') : filter.value,
      }));

      queryPayload.attributeFilters = encodeURI(JSON.stringify(dynamicFilters));
    }

    const res = await extensions.product.query(queryPayload);
    return res;

    // const params = new URLSearchParams();

    // if (productQuery.query) params.set('query', productQuery.query);
    // if (productQuery.limit) params.set('limit', productQuery.limit);

    // const res = await fetchApiHub(`/action/product/query?${params.toString()}`, SDK.locale);

    // return { data: res, isError: false } as SDKResponse<Result>;
  }, []);

  const searchProducts = useCallback(async (productQuery: ProductSearchQuery): Promise<SDKResponse<Result>> => {
    const res = await sdk.callAction({ actionName: 'plp/search', payload: productQuery });
    return res as SDKResponse<Result>;
  }, []);

  const getProductBySku = useCallback(async (productQuery: any) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.product.getProduct(productQuery);
    return res;
  }, []);
  const getInventory = useCallback(async (sku: string) => {
    const response = await sdk.callAction({ actionName: 'product/getInventory', query: { sku } });
    return (response.isError ? {} : response.data) as Inventory;
  }, []);

  const fetchProductByCategory = useCallback(async (id: string) => {
    const response = await sdk.callAction({
      actionName: ProductActions.fetchProductByCategory,
      query: { id },
    });
    return (response.isError ? {} : response.data) as ProductData;
  }, []);
  const getReviews = useCallback(async () => {
    const response = await sdk.callAction({ actionName: ReviewsActions.getReviews, query: {} });
    return (response.isError ? {} : response.data) as ReviewData;
  }, []);
  const getAllReviewsByReviewSource = useCallback(async (reviewSource: string) => {
    const response = await sdk.callAction({
      actionName: ReviewsActions.getAllReviewsByReviewSource,
      query: { reviewSource },
    });
    return (response.isError ? {} : response.data) as ReviewData;
  }, []);
  const getReviewByKey = useCallback(async (key: string) => {
    const response = await sdk.callAction({ actionName: ReviewsActions.getReviewByKey, query: { key } });
    return (response.isError ? {} : response.data) as ReviewData;
  }, []);
  const getAllReviewByProductId = useCallback(
    async (key: string, limit: number = 3, offset: number = 0, sort: string) => {
      const response = await sdk.callAction({
        actionName: ReviewsActions.getAllReviewByProductId,
        query: { key, limit, offset, sort },
      });
      return (response.isError ? {} : response.data) as ReviewData;
    },
    [],
  );
  const getBraSizeCalcData = useCallback(async () => {
    const response = await sdk.callAction({ actionName: 'product/getCustomObjectBraCalcData' });
    return response;
  }, []);
  const getReviewByAssociatedContentId = useCallback(async (key: string) => {
    const response = await sdk.callAction({
      actionName: ReviewsActions.getReviewByAssociatedContentId,
      query: { key },
    });
    return (response.isError ? {} : response.data) as ReviewData;
  }, []);
  const createReview = useCallback(async (body: ReviewRequestData) => {
    const payload: ReviewRequestData = {
      key: body?.key,
      authorName: body?.authorName,
      title: body?.title,
      text: body?.text,
      reviewSource: body?.reviewSource,
      rating: body?.rating,
      typeId: body?.typeId,
      id: body?.id,
    };
    const response = await sdk.callAction({ actionName: ReviewsActions.createReview, payload: payload });
    return (response.isError ? { status: false } : response.data) as ReviewDraft;
  }, []);
  const updateReviewByKey = useCallback(async (body: Action) => {
    const payload: Action = {
      id: body?.id,
      key: body?.key,
      version: body?.version,
      action: body?.action,
      rating: body?.rating,
    };
    const response = await sdk.callAction({ actionName: ReviewsActions.updateReviewByKey, payload: payload });
    return (response.isError ? { status: false } : response.data) as ReviewDraft;
  }, []);
  const updateReviewByAssociatedContentId = useCallback(async (body: Action) => {
    const payload: Action = {
      id: body?.id,
      key: body?.key,
      version: body?.version,
      action: body?.action,
      rating: body?.rating,
    };
    const response = await sdk.callAction({
      actionName: ReviewsActions.updateReviewByAssociatedContentId,
      payload: payload,
    });
    return (response.isError ? { status: false } : response.data) as ReviewDraft;
  }, []);
  const getAllReviewBySku = useCallback(async (sku: string, limit: number = 3, offset: number = 0, sort: string) => {
    const response = await sdk.callAction({
      actionName: ReviewsActions.getAllReviewBySku,
      query: { sku, limit, offset, sort },
    });
    return (response.isError ? {} : response.data) as ReviewData;
  }, []);
  const getSearchQueryVariant = useCallback(async (searchQuery?: SearchQueryVariants) => {
    const payload = {
      query: searchQuery?.query,
      type: searchQuery?.type,
      limit: searchQuery?.limit,
      modulePositiveTags: searchQuery?.modulePositiveTags ? searchQuery?.modulePositiveTags : '',
      slug: searchQuery?.slug,
      offset: searchQuery?.offset,
    };
    const res = await sdk.callAction({ actionName: 'product/search', payload: payload });
    return res;
  }, []);

  const getProductAssociatedWithPositiveTags = useCallback(async (payload: { tags: string[]; limit?: number }) => {
    const res = await sdk.callAction({ actionName: 'product/getProductAssociatedWithPositiveTags', payload: payload });
    return res;
  }, []);

  const getSalesSupportData = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'content/getSalesSupportContent' });
    return res;
  }, []);

  const updateReviewCounts = useCallback(async (payload: { entryId: string; isPositive: boolean }) => {
    const res = await sdk.callAction({ actionName: 'content/updateCounts', payload: payload });
    return res;
  }, []);

  const getCustomObjectsPayments = useCallback(async () => {
    const response = await paymentService.paymentMethodsList(locale);
    return response;
  }, []);
  const sendContactForm = useCallback(
    async (
      subject: string,
      artikel: string,
      message: string,
      customernumber: string,
      email: string,
      firstname: string,
      lastname: string,
      files?: { name: string; type: string; base64: string }[],
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/sendContactForm',
        payload: { subject, artikel, message, customernumber, email, firstname, lastname, files },
      });
      return response;
    },
    [],
  );
  const magazineArticleQuery = useCallback(
    async (
      tags: { positive: string[]; negative: string[] },
      modulePositiveTags: string[],
      moduleNegativeTags: string[],
      skip: number,
      limit: number,
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/magazineArticleQuery',
        payload: {
          tags,
          modulePositiveTags,
          moduleNegativeTags,
          skip,
          limit,
        },
      });
      return response;
    },
    [],
  );
  const recipeArticleQuery = useCallback(
    async (
      tags: { positive: string[]; negative: string[] },
      modulePositiveTags: string[],
      moduleNegativeTags: string[],
      skip: number,
      limit: number,
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/recipeArticleQuery',
        payload: {
          tags,
          modulePositiveTags,
          moduleNegativeTags,
          skip,
          limit,
        },
      });
      return response;
    },
    [],
  );

  const getDataForPage = useCallback(async (slug: string) => {
    const response = await sdk.callAction({
      actionName: 'product/getDataForPage',
      payload: { slug },
    });
    return response;
  }, []);

  const getModulesData = useCallback(
    async (
      entityId: string,
      tags?: { positive: string[]; negative: string[] },
      modulePositiveTags?: string[],
      moduleNegativeTags?: string[],
    ) => {
      const response = await sdk.callAction({
        actionName: 'product/getModulesData',
        payload: {
          entityId,
          tags,
          modulePositiveTags,
          moduleNegativeTags,
        },
      });
      return response;
    },
    [],
  );

  return {
    categories,
    query,
    getInventory,
    fetchProductByCategory,
    getReviews,
    getAllReviewsByReviewSource,
    createReview,
    getReviewByKey,
    getReviewByAssociatedContentId,
    updateReviewByKey,
    getProductBySku,
    getSearchQueryVariant,
    updateReviewByAssociatedContentId,
    getAllReviewByProductId,
    getProductAssociatedWithPositiveTags,
    getSalesSupportData,
    updateReviewCounts,
    getBraSizeCalcData,
    getCustomObjectsPayments,
    sendContactForm,
    magazineArticleQuery,
    recipeArticleQuery,
    getDataForPage,
    searchProducts,
    getModulesData,
    getAllReviewBySku,
  };
};

export default useProduct;
